import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Search, X } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, EmptyState, Input, Skeleton, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { useAsync } from '../lib/useAsync';
import {
  createExercise,
  createWorkout,
  deleteWorkout,
  getExerciseFilters,
  getPrograms,
  getWorkouts,
  searchAdminExercises,
  updateWorkout,
  type AdminExercise,
  type AdminWorkout,
  type AdminWorkoutItem,
} from '../lib/adminApi';

export function ProgramWorkoutsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const load = useCallback(() => getWorkouts(id), [id]);
  const { data, loading, error, reload } = useAsync(load);
  const { data: programs } = useAsync(getPrograms);
  const program = (programs ?? []).find((p) => p.id === id);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = data ?? [];

  if (adding) {
    return (
      <FormShell title={`New workout · ${program?.title ?? ''}`} onBack={() => setAdding(false)}>
        <WorkoutForm programId={id} mode="create" onCancel={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />
      </FormShell>
    );
  }
  const editing = list.find((w) => w.id === editingId);
  if (editing) {
    return (
      <FormShell title={`Edit workout · ${program?.title ?? ''}`} onBack={() => setEditingId(null)}>
        <WorkoutForm programId={id} mode="edit" workout={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  return (
    <section className="mt-6 animate-fade-up">
      <Link to="/programs" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Programs
      </Link>
      <div className="mt-3 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">{program?.title ?? 'Workouts'}</h1>
          <p className="mt-1 text-ink-soft">Week-by-week sessions for this program.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> New workout
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : error ? (
        <EmptyState title="Couldn't load workouts" description={error} />
      ) : list.length === 0 ? (
        <div className="mt-4">
          <EmptyState title="No workouts yet" description="Add a session, then search the exercise library to fill it." />
          <div className="mt-3 flex justify-center">
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden /> New workout
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {list.map((w) => (
            <WorkoutCard key={w.id} w={w} onEdit={() => setEditingId(w.id)} onDeleted={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function WorkoutCard({ w, onEdit, onDeleted }: { w: AdminWorkout; onEdit: () => void; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!(await confirm({ title: `Delete "${w.name}"?`, description: 'This cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setBusy(true);
    try {
      await deleteWorkout(w.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-control bg-dark text-on-dark text-sm font-semibold">
        W{w.weekNo}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">Day {w.dayNo} · {w.name}</p>
        <p className="text-xs text-muted">{w.items.length} exercises{w.targetAreas.length ? ` · ${w.targetAreas.join(', ')}` : ''}</p>
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" loading={busy} onClick={remove} className="text-bad-ink">Delete</Button>
    </Card>
  );
}

interface DraftItem {
  exerciseId: string;
  exerciseName: string;
  image: string | null;
  sets: string;
  reps: string;
  rest: string;
}

function toDraft(it: AdminWorkoutItem): DraftItem {
  return {
    exerciseId: it.exerciseId,
    exerciseName: it.exerciseName,
    image: it.image,
    sets: it.sets != null ? String(it.sets) : '3',
    reps: it.reps ?? '12',
    rest: it.rest ?? '60s',
  };
}

function WorkoutForm({
  programId,
  mode,
  workout,
  onCancel,
  onDone,
}: {
  programId: string;
  mode: 'create' | 'edit';
  workout?: AdminWorkout;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(workout?.name ?? '');
  const [weekNo, setWeekNo] = useState(String(workout?.weekNo ?? 1));
  const [dayNo, setDayNo] = useState(String(workout?.dayNo ?? 1));
  const [targetAreas, setTargetAreas] = useState((workout?.targetAreas ?? []).join(', '));
  const [items, setItems] = useState<DraftItem[]>((workout?.items ?? []).map(toDraft));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addExercise(o: AdminExercise) {
    if (items.some((i) => i.exerciseId === o.id)) return;
    setItems((prev) => [...prev, { exerciseId: o.id, exerciseName: o.name, image: o.image, sets: '3', reps: '12', rest: '60s' }]);
  }
  function updateItem(idx: number, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    setError(null);
    if (!name.trim()) return setError('Give the workout a name.');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        weekNo: Number(weekNo) || 1,
        dayNo: Number(dayNo) || 1,
        targetAreas: targetAreas.split(',').map((s) => s.trim()).filter(Boolean),
        items: items.map((i) => ({ exerciseId: i.exerciseId, sets: Number(i.sets) || null, reps: i.reps || null, rest: i.rest || null })),
      };
      if (mode === 'create') await createWorkout(programId, body);
      else await updateWorkout(workout!.id, body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the workout.');
      setSaving(false);
    }
  }

  return (
    <Card className="mb-1 flex flex-col gap-3 p-5">
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Body Foundations" />
      <div className="grid gap-3 sm:grid-cols-3">
        <Input label="Week" type="number" inputMode="numeric" value={weekNo} onChange={(e) => setWeekNo(e.target.value)} />
        <Input label="Day" type="number" inputMode="numeric" value={dayNo} onChange={(e) => setDayNo(e.target.value)} />
        <Input label="Target areas" value={targetAreas} onChange={(e) => setTargetAreas(e.target.value)} placeholder="Core" />
      </div>

      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-muted">Exercises</p>
      <div className="flex flex-col gap-2">
        {items.map((it, idx) => (
          <div key={it.exerciseId} className="flex items-end gap-2 rounded-control border border-line bg-elevated p-2">
            {it.image ? <img src={it.image} alt="" className="size-10 shrink-0 rounded object-cover" /> : <span className="size-10 shrink-0 rounded bg-surface" />}
            <span className="mb-1.5 min-w-0 flex-1 truncate self-end text-sm font-medium text-ink">{it.exerciseName}</span>
            <PrescriptionField label="Sets" width="w-14" value={it.sets} placeholder="3" onChange={(v) => updateItem(idx, { sets: v })} />
            <PrescriptionField label="Reps" width="w-16" value={it.reps} placeholder="12" onChange={(v) => updateItem(idx, { reps: v })} />
            <PrescriptionField label="Rest" width="w-16" value={it.rest} placeholder="60s" onChange={(v) => updateItem(idx, { rest: v })} />
            <button type="button" aria-label="Remove" onClick={() => removeItem(idx)} className="mb-0.5 grid size-7 shrink-0 place-items-center rounded text-muted hover:bg-surface hover:text-bad-ink">
              <X className="size-4" aria-hidden />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted">No exercises yet. Search and add some below.</p>}
      </div>

      <ExercisePicker onAdd={addExercise} chosen={new Set(items.map((i) => i.exerciseId))} />

      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}

/** A captioned mini-input for a workout line's sets / reps / rest. */
function PrescriptionField({ label, value, onChange, placeholder, width }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; width: string }) {
  return (
    <label className="flex shrink-0 flex-col items-center gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</span>
      <input
        aria-label={label}
        placeholder={placeholder}
        className={`${width} rounded border border-line bg-card px-2 py-1 text-center text-sm`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

const pickerSelect =
  'rounded-control border border-line bg-elevated px-2.5 py-1.5 text-sm text-ink outline-none focus:border-ink-soft';

function ExercisePicker({ onAdd, chosen }: { onAdd: (o: AdminExercise) => void; chosen: Set<string> }) {
  const [q, setQ] = useState('');
  const [muscle, setMuscle] = useState('');
  const [equipment, setEquipment] = useState('');
  const [category, setCategory] = useState('');
  const [results, setResults] = useState<AdminExercise[]>([]);
  const [creating, setCreating] = useState(false);
  const { data: filters } = useAsync(getExerciseFilters);

  const hasQuery = q.trim().length >= 2 || !!muscle || !!equipment || !!category;

  useEffect(() => {
    if (!hasQuery) {
      setResults([]);
      return;
    }
    let active = true;
    searchAdminExercises({ q: q.trim() || undefined, muscleGroup: muscle || undefined, equipment: equipment || undefined, category: category || undefined })
      .then((r) => active && setResults(r))
      .catch(() => active && setResults([]));
    return () => {
      active = false;
    };
  }, [q, muscle, equipment, category, hasQuery]);

  const exactMatch = results.some((r) => r.name.toLowerCase() === q.trim().toLowerCase());
  const canCreate = q.trim().length >= 2 && !exactMatch;

  async function createAndAdd() {
    setCreating(true);
    try {
      const created = await createExercise({
        name: q.trim(),
        equipment: equipment || null,
        muscleGroups: muscle ? [muscle] : [],
      });
      onAdd(created);
      setQ('');
    } catch {
      /* surfaced by the form-level error if the save fails on submit */
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="rounded-control border border-dashed border-line-strong p-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the exercise library…"
          className="w-full rounded-control border border-line bg-elevated py-2 pl-9 pr-3 text-sm text-ink outline-none focus:border-ink-soft"
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <select className={pickerSelect} value={muscle} onChange={(e) => setMuscle(e.target.value)}>
          <option value="">Any muscle</option>
          {(filters?.muscleGroups ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className={pickerSelect} value={equipment} onChange={(e) => setEquipment(e.target.value)}>
          <option value="">Any equipment</option>
          {(filters?.equipment ?? []).map((eq) => <option key={eq} value={eq}>{eq}</option>)}
        </select>
        <select className={pickerSelect} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Any type</option>
          {(filters?.categories ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {results.length > 0 && (
        <div className="mt-2 flex max-h-56 flex-col gap-1 overflow-auto">
          {results.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onAdd(o)}
              disabled={chosen.has(o.id)}
              className="flex items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm hover:bg-surface disabled:opacity-40"
            >
              {o.image ? <img src={o.image} alt="" className="size-8 shrink-0 rounded object-cover" /> : <span className="size-8 shrink-0 rounded bg-surface" />}
              <span className="min-w-0 flex-1 truncate text-ink">{o.name}</span>
              <span className="shrink-0 text-xs capitalize text-muted">{o.equipment ?? ''}</span>
              {chosen.has(o.id) ? <span className="text-xs text-muted">Added</span> : <Plus className="size-4 text-muted" aria-hidden />}
            </button>
          ))}
        </div>
      )}
      {hasQuery && results.length === 0 && !canCreate && (
        <p className="mt-2 px-1 text-sm text-muted">No matching exercises.</p>
      )}
      {canCreate && (
        <button
          type="button"
          onClick={createAndAdd}
          disabled={creating}
          className="mt-2 flex w-full items-center gap-2 rounded-control border border-dashed border-line-strong px-2 py-2 text-left text-sm text-ink hover:bg-surface disabled:opacity-50"
        >
          <Plus className="size-4 text-muted" aria-hidden />
          {creating ? 'Adding…' : <span>Add “<span className="font-medium">{q.trim()}</span>” as a new exercise</span>}
        </button>
      )}
    </div>
  );
}
